/**
 * Keeping a web session alive, and ending one without ending a call.
 *
 * Access tokens live eight hours. Login has always returned a 30-day rotating
 * refresh token as well, but the web app dropped it, so every agent was signed
 * out eight hours after they signed in — by a hard `location.replace('/login')`
 * from whichever request noticed first. Logout tears the softphone down, so an
 * agent who was on the phone at that moment hung up on the customer (Proven
 * Plumbing, 2026-10-01).
 *
 * Two independent guarantees, because the first cannot cover every tab:
 *
 *  1. Refresh. The tab that signed in holds the refresh token — in memory only,
 *     never localStorage (see `auth.store` partialize) — and renews the access
 *     token a few minutes before it lapses. The new token is persisted as
 *     before, so other tabs adopt it instead of renewing themselves. Only one
 *     tab ever holds a given refresh token: the server treats a reused one as a
 *     replay and revokes every session the user has, so tabs must never share
 *     one. A reload loses it, and that tab then lives on adopted tokens or
 *     until the eight hours are up.
 *  2. A session that does end waits for the calls to. The sign-out is deferred
 *     while this tab has a call up, and runs the moment the last one ends.
 */
import axios from 'axios';
import { toast } from 'sonner';
import { AUTH_STORE_KEY, useAuthStore } from '@/stores/auth.store';
import { decodeJwtPayload, isTokenExpired } from '@/lib/auth';
import { hasCallsInFlight, whenNoCallsInFlight } from '@/lib/voip/callsInFlight';
import { authEndpoints } from './endpoints';

/** Renew this long before the access token's `exp`. Background tabs throttle
 *  timers to about one a minute, so this is several of those. */
export const REFRESH_LEAD_MS = 5 * 60_000;
/** After a refresh that failed for a reason other than a dead token. */
export const REFRESH_RETRY_MS = 60_000;

const SESSION_EXPIRED_TOAST_ID = 'session-expired';

/** Bare client: the operations client's 401 handler must not see the refresh
 *  call, and nothing here may carry the token being replaced. Exported for
 *  tests. */
export const refreshClient = axios.create({
  baseURL: import.meta.env.VITE_OPERATIONS_URL ?? '',
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// ─── Ending a session ────────────────────────────────────────────────────────

/**
 * NOT-715 — a session ends once, however many requests discover it. Polling
 * queries deliver several 401s within the same few hundred milliseconds when a
 * token lapses, and each one used to log out, navigate and toast again.
 * Deliberately never reset: the document is about to navigate away, and
 * re-arming it would only let a late 401 fire a second redirect during
 * teardown.
 */
let sessionEnding = false;
let deferredForCall = false;

function signOut(): void {
  useAuthStore.getState().logout();
  window.location.replace('/login');
}

/**
 * End the session — after the calls, if any are up. Logout hangs up whatever
 * the softphone holds, and an agent mid-sentence with a customer must not be
 * cut off because a token expired; the call itself does not need the token.
 */
export function redirectToLogin(): void {
  if (sessionEnding) return;
  sessionEnding = true;
  if (!hasCallsInFlight()) {
    signOut();
    return;
  }
  deferredForCall = true;
  toast.warning('Your session expired. You will be signed out when this call ends.', {
    id: SESSION_EXPIRED_TOAST_ID,
    duration: Infinity,
  });
  whenNoCallsInFlight(signOut);
}

/** True while a lapsed session is waiting for a call to end. The per-request
 *  "Session expired" toasts are held back then, so they cannot replace the
 *  notice that says why the user is still signed in. */
export function isSessionEndDeferred(): boolean {
  return deferredForCall;
}

// ─── Recovering a session ────────────────────────────────────────────────────

let refreshInFlight: Promise<string | null> | null = null;

/**
 * Exchange the in-memory refresh token for a new access token. Single-flight:
 * the server rotates the refresh token on every use and revokes the user's
 * sessions if an old one is presented again, so two concurrent renewals from
 * this tab would sign the user out everywhere.
 *
 * Resolves to the new access token, or null. A 400/401 means the refresh token
 * is dead, and it is dropped so nothing retries it; anything else (network,
 * 429, 5xx) leaves it in place for the next attempt.
 */
export function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;
  const { refreshToken, impersonation } = useAuthStore.getState();
  if (!refreshToken || impersonation.active) return Promise.resolve(null);

  refreshInFlight = refreshClient
    .post<{ accessToken: string; refreshToken: string }>(authEndpoints.refresh(), {
      refreshToken,
    })
    .then((res) => {
      // Signed out (or in as someone else) while this was in the air: the
      // answer belongs to a session that no longer exists here.
      if (useAuthStore.getState().refreshToken !== refreshToken) return null;
      useAuthStore.getState().setToken(res.data.accessToken, res.data.refreshToken);
      return res.data.accessToken;
    })
    .catch((error: unknown) => {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (
        (status === 400 || status === 401) &&
        useAuthStore.getState().refreshToken === refreshToken
      ) {
        useAuthStore.setState({ refreshToken: null });
      }
      return null;
    })
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

interface PersistedAuth {
  token?: string | null;
  userId?: string | null;
  tenantId?: string | null;
  impersonation?: { active?: boolean } | null;
}

function readPersistedAuth(): PersistedAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_STORE_KEY);
    if (!raw) return null;
    return (JSON.parse(raw) as { state?: PersistedAuth }).state ?? null;
  } catch {
    return null;
  }
}

/**
 * Take a token another tab renewed, if it is for this same user and outlives
 * the one this tab has. Never switches identity: a different user signed in
 * elsewhere is not a session this tab may continue. Never crosses into or out
 * of impersonation either — an impersonated user and that user's own session
 * share a userId and tenantId, but a tab showing the impersonation banner
 * must not start sending the real user's token, nor the reverse. Only normal
 * sessions renew anyway (impersonation has no refresh token). Never moves
 * backwards: every tab persists its whole auth state on any write, so a tab
 * still holding the older token can put it back in localStorage.
 */
export function adoptPersistedToken(rejected: string | null): string | null {
  const persisted = readPersistedAuth();
  const mine = useAuthStore.getState();
  const token = persisted?.token;
  if (
    !token ||
    token === rejected ||
    token === mine.token ||
    persisted.userId !== mine.userId ||
    persisted.tenantId !== mine.tenantId ||
    persisted.impersonation?.active ||
    mine.impersonation.active ||
    isTokenExpired(token)
  ) {
    return null;
  }
  const theirs = expiresAtMs(token) ?? 0;
  const ours = (mine.token && expiresAtMs(mine.token)) || 0;
  if (theirs <= ours) return null;
  mine.setToken(token);
  return token;
}

/**
 * Find a usable access token after `rejected` was refused: one this tab has
 * already moved on to, one another tab renewed, or a fresh one from the
 * refresh token. Null means the session is over.
 */
export async function recoverSession(rejected: string | null): Promise<string | null> {
  const current = useAuthStore.getState().token;
  if (current && current !== rejected && !isTokenExpired(current)) return current;
  return adoptPersistedToken(rejected) ?? (await refreshAccessToken());
}

// ─── Keeping it alive ────────────────────────────────────────────────────────

function expiresAtMs(token: string): number | null {
  const exp = decodeJwtPayload<{ exp?: unknown }>(token)?.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

/**
 * Renew ahead of expiry in the tab that can, and pick up renewals from the
 * tabs that did. Returns the teardown. Mounted once per dashboard tab.
 */
export function startSessionKeepAlive(): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const schedule = () => {
    clear();
    const { token, refreshToken } = useAuthStore.getState();
    if (!token || !refreshToken) return;
    const expiresAt = expiresAtMs(token);
    if (expiresAt === null) return;
    timer = setTimeout(
      () => {
        timer = null;
        void refreshAccessToken().then((fresh) => {
          // Success reschedules through the store subscription below. A
          // transient failure keeps the refresh token; try again shortly.
          if (!fresh && useAuthStore.getState().refreshToken) {
            timer = setTimeout(schedule, REFRESH_RETRY_MS);
          }
        });
      },
      Math.max(expiresAt - REFRESH_LEAD_MS - Date.now(), 0),
    );
  };

  const unsubscribe = useAuthStore.subscribe((state, prev) => {
    if (state.token !== prev.token || state.refreshToken !== prev.refreshToken) schedule();
  });

  // Another tab persisted a renewed token. Take it now, so this tab's next
  // store write cannot put the older one back in localStorage.
  const onStorage = (event: StorageEvent) => {
    if (event.key === AUTH_STORE_KEY) adoptPersistedToken(null);
  };
  window.addEventListener('storage', onStorage);

  schedule();
  return () => {
    clear();
    unsubscribe();
    window.removeEventListener('storage', onStorage);
  };
}
